import { isAfter, isSameDay } from 'date-fns';
import { isBetween } from 'shared/lib/date-fns';
import { HabitSchedule, ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { v4 as uuidv4 } from 'uuid';

import { getSchedulePlanning } from './get-schedule-due-date';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframeByDate } from './get-schedule-timeframe-by-date';

export type Options = {
  id?: ID;
  timeslot?: Timeslot | null;
  originalDate?: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const createPlannedOccurrenceForDate = (
  schedules: HabitSchedule[],
  newDate: Timestamp,
  { id, timeslot, originalDate, today, weekStartsOn }: Options,
): HabitSchedule[] => {
  // get the affected two schedules
  const updatedSchedules = structuredClone(schedules).sort(
    (scheduleA, scheduleB) =>
      scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
  );
  const originScheduleIndex =
    id || originalDate
      ? updatedSchedules.findIndex((schedule) =>
          id
            ? schedule.plannedOccurrences?.find(
                (plannedOccurrence) => plannedOccurrence.id === id,
              )
            : schedule.endDate
              ? isBetween(
                  originalDate!,
                  schedule.startDate,
                  getScheduleEndDate(schedule)!,
                )
              : isSameDay(originalDate!, schedule.startDate) ||
                isAfter(originalDate!, schedule.startDate),
        )
      : -1;

  let newScheduleIndex = updatedSchedules.findIndex((schedule) =>
    schedule?.endDate
      ? isBetween(newDate, schedule.startDate, getScheduleEndDate(schedule)!)
      : isSameDay(newDate, schedule.startDate) ||
        isAfter(newDate, schedule.startDate),
  );
  // if there is no schedule that matches the date, the date is before the first schedule, which means the first schedule should be taken
  newScheduleIndex = newScheduleIndex < 0 ? 0 : newScheduleIndex;

  // moving from one schedule to another means we have to update the origin schedule as well
  if (originScheduleIndex !== newScheduleIndex && originScheduleIndex >= 0) {
    const origSchedule = updatedSchedules[originScheduleIndex];
    const plannedOccurrences = (origSchedule.plannedOccurrences =
      origSchedule.plannedOccurrences ?? []);
    // get the planning of the schedule for the timeframe the originalDate represents
    const plannedOccurrence = plannedOccurrences.find(
      (plannedOccurrence) =>
        plannedOccurrence.id === id ||
        (originalDate && isSameDay(plannedOccurrence.date, originalDate)),
    );
    const _originDate = plannedOccurrence?.date ?? originalDate;
    const timeframe = _originDate
      ? getScheduleTimeframeByDate(origSchedule, _originDate, weekStartsOn)
      : undefined;

    // get all plannedOccurrences for the timeframe, to filter out the originalDate
    const timeframeOccurrences =
      timeframe && plannedOccurrences.length
        ? plannedOccurrences.filter(({ date }) =>
            isBetween(date, timeframe.startDate, timeframe.endDate),
          )
        : [];

    // if there are multiple planned occurrences, we can just filter the one out with the originalDate
    if (timeframeOccurrences.length > 1) {
      origSchedule.plannedOccurrences =
        plannedOccurrences.filter(
          ({ date }) => !isSameDay(date, _originDate!),
        ) ?? [];
    }
    // if there is only one planned occurrence, we need to add a skip for that date
    if (timeframeOccurrences.length === 1) {
      origSchedule.entries = [
        ...origSchedule.entries.filter(
          ({ date }) => !isSameDay(date, _originDate!),
        ),
        {
          date: _originDate!,
          id: uuidv4(),
          state: ScheduleEntryState.Skip,
          value: null,
          hidden: true,
        },
      ];
      origSchedule.completions = origSchedule.completions?.filter(
        (completion) => !isSameDay(completion, _originDate!),
      );
      origSchedule.skips = [...(origSchedule.skips ?? []), _originDate!];
    }
    // if there are no planned occurrences, we have to create new ones for the timeframe and filter out the originalDate
    if (timeframe && !timeframeOccurrences.length) {
      const newTimeframePlannedOccurrences = getSchedulePlanning(origSchedule, {
        ...timeframe,
        today,
        weekStartsOn,
        // filter out the date (or originalDate), as this entry has to be replaced with the new values
      }).filter(
        (plannedOccurrence) => !isSameDay(plannedOccurrence.date, _originDate!),
      );
      const combinedOccurrences = [
        ...plannedOccurrences,
        ...newTimeframePlannedOccurrences,
      ];
      origSchedule.plannedOccurrences = combinedOccurrences.filter(
        (occurrenceA) =>
          !combinedOccurrences.some(
            (occurrenceB) =>
              occurrenceA.id !== occurrenceB.id &&
              isSameDay(occurrenceA.date, occurrenceB.date),
          ),
      );
    }
  }

  // handling the new schedule
  const newSchedule = updatedSchedules[newScheduleIndex];
  const plannedOccurrences = (newSchedule.plannedOccurrences =
    newSchedule.plannedOccurrences ?? []);
  const _timeslot = timeslot ?? null;
  // get the timeframe the new date will be placed in
  const newTimeframe = getScheduleTimeframeByDate(
    newSchedule,
    newDate,
    weekStartsOn,
  );

  // if the origin is in the same schedule, we need to check the timeframe it is in
  const oldPlannedOccurrence = plannedOccurrences.find(
    (plannedOccurrence) =>
      plannedOccurrence.id === id ||
      (originalDate && isSameDay(plannedOccurrence.date, originalDate)),
  );
  const _originDate = oldPlannedOccurrence?.date ?? originalDate;
  const oldTimeframe = _originDate
    ? getScheduleTimeframeByDate(newSchedule, _originDate, weekStartsOn)
    : undefined;
  const timeframesAreIdentical =
    oldTimeframe &&
    isSameDay(newTimeframe.startDate, oldTimeframe?.startDate) &&
    isSameDay(newTimeframe.endDate, oldTimeframe.endDate);

  // get all plannedOccurrences for the timeframe, to filter out the originalDate
  const oldTimeframeOccurrences =
    oldTimeframe && plannedOccurrences.length
      ? plannedOccurrences.filter(({ date }) =>
          isBetween(date, oldTimeframe.startDate, oldTimeframe.endDate),
        )
      : [];
  const plannedOccurrenceNeedsToBeCopied =
    !timeframesAreIdentical && oldTimeframeOccurrences.length === 1;

  // if there is only one planned occurrence, we need to add a skip for that date
  if (plannedOccurrenceNeedsToBeCopied) {
    newSchedule.entries = [
      ...newSchedule.entries.filter(
        ({ date }) => !isSameDay(date, _originDate!),
      ),
      {
        date: _originDate!,
        id: uuidv4(),
        state: ScheduleEntryState.Skip,
        value: null,
        hidden: true,
      },
    ];
    newSchedule.completions = newSchedule.completions?.filter(
      (completion) => !isSameDay(completion, _originDate!),
    );
    newSchedule.skips = [...(newSchedule.skips ?? []), _originDate!];
  }
  // if there are no planned occurrences, we have to create new ones for the timeframe and filter out the originalDate
  if (
    !timeframesAreIdentical &&
    oldTimeframe &&
    !oldTimeframeOccurrences.length
  ) {
    const initialNewTimeframePlannedOccurrences = getSchedulePlanning(
      newSchedule,
      {
        ...oldTimeframe,
        today,
        weekStartsOn,
      },
    );

    const hasOriginal = initialNewTimeframePlannedOccurrences.some((o) =>
      isSameDay(o.date, _originDate!),
    );
    const isSingleImplicitOccurrence =
      initialNewTimeframePlannedOccurrences.length === 1 && hasOriginal;

    if (isSingleImplicitOccurrence) {
      // Single implicit occurrence: COPY strategy (Skip Old, Keep Old, Add New)
      newSchedule.entries = [
        ...newSchedule.entries.filter(
          ({ date }) => !isSameDay(date, _originDate!),
        ),
        {
          date: _originDate!,
          id: uuidv4(),
          state: ScheduleEntryState.Skip,
          value: null,
          hidden: true,
        },
      ];
      newSchedule.completions = newSchedule.completions?.filter(
        (completion) => !isSameDay(completion, _originDate!),
      );
      newSchedule.skips = [...(newSchedule.skips ?? []), _originDate!];
    }

    const newTimeframePlannedOccurrences =
      initialNewTimeframePlannedOccurrences.filter(
        (plannedOccurrence) =>
          (isSingleImplicitOccurrence
            ? true // Keep original if single
            : !isSameDay(plannedOccurrence.date, _originDate!)) && // Filter original if multiple
          !isSameDay(plannedOccurrence.date, newDate!),
      );

    const combinedOccurrences = [
      ...(newSchedule.plannedOccurrences ?? []),
      ...newTimeframePlannedOccurrences,
    ];
    newSchedule.plannedOccurrences = combinedOccurrences.filter(
      (occurrenceA) =>
        !combinedOccurrences.some(
          (occurrenceB) =>
            occurrenceA.id !== occurrenceB.id &&
            isSameDay(occurrenceA.date, occurrenceB.date),
        ),
    );
  }

  // as the user moves a schedule to a specific day, we need to filter out a skip on the entries on that day, if it exists
  newSchedule.entries = newSchedule.entries.filter(
    (entry) =>
      !(
        entry.state === ScheduleEntryState.Skip &&
        isSameDay(entry.date, newDate)
      ),
  );
  newSchedule.skips = newSchedule.skips?.filter(
    (skip) => !isSameDay(skip, newDate),
  );

  // if we have an id, it could mean we have to update the occurrence with that specific id
  const occurrenceToUpdate = id
    ? plannedOccurrences.find(
        (plannedOccurrence) => plannedOccurrence.id === id,
      )
    : // if we have an originalDate, it could mean we have to find the occurrence for the specific date
      originalDate
      ? plannedOccurrences.find(({ date }) => isSameDay(date, originalDate))
      : undefined;
  if (occurrenceToUpdate && !plannedOccurrenceNeedsToBeCopied) {
    // filter out the planned occurrence of the new date, if it exists
    newSchedule.plannedOccurrences = plannedOccurrences.filter(
      (occurrence) =>
        occurrence.id === occurrenceToUpdate.id ||
        !isSameDay(occurrence.date, newDate),
    );
    occurrenceToUpdate.date = newDate;
    occurrenceToUpdate.timeslot = _timeslot;
    return updatedSchedules;
  }

  //  if we are not updating a planned occurrence and there are already planned occurrences, we just have to add this one
  const timeframePlannedOccurrences = (
    newSchedule.plannedOccurrences ?? []
  ).filter(
    (occurrence) =>
      isBetween(
        occurrence.date,
        newTimeframe.startDate,
        newTimeframe.endDate,
      ) && !isSameDay(occurrence.date, newDate),
  );

  if (timeframePlannedOccurrences.length) {
    newSchedule.plannedOccurrences = [
      ...(newSchedule.plannedOccurrences ?? []).filter(
        ({ date }) => !isSameDay(date, newDate),
      ),
      {
        date: newDate,
        timeslot: _timeslot,
        id: uuidv4(),
      },
    ];

    return updatedSchedules;
  }

  // if there are no planned occurrences, we have to create them for all occurrences
  const newTimeframePlannedOccurrences = getSchedulePlanning(newSchedule, {
    ...newTimeframe,
    today,
    weekStartsOn,
    strictEndDate: true,
  }).filter((plannedOccurrence) =>
    // filter out the date (or originalDate), as this entry has to be replaced with the new values
    originalDate
      ? !isSameDay(plannedOccurrence.date, originalDate)
      : !isSameDay(plannedOccurrence.date, newDate),
  );

  // add the new planned occurrence
  newTimeframePlannedOccurrences.push({
    id: uuidv4(),
    date: newDate,
    timeslot: _timeslot,
  });

  const combinedOccurrences = [
    ...(newSchedule.plannedOccurrences ?? []),
    ...newTimeframePlannedOccurrences,
  ];

  // make sure there are no duplicate entries on the plannedOccurrences
  const seenIds = new Set<string>();
  const seenTimestamps = new Set<number>();
  newSchedule.plannedOccurrences = combinedOccurrences.filter((occ) => {
    const timestamp = occ.date.getTime();
    if (seenIds.has(occ.id) || seenTimestamps.has(timestamp)) {
      return false;
    }
    seenIds.add(occ.id);
    seenTimestamps.add(timestamp);
    return true;
  });

  return updatedSchedules;
};
